import React, { FC } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { ButtonTypes, StandardButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { RadioButtonCards, RadioButtonPanels } from '@eika/radio-button';

import { useConfig } from '../default-config';
import { useTranslation } from '../translations';

import husMSkyer from '../resources/hus-m-skyer.svg';

enum FormaalType {
    KJOEPE_FINBEVIS = 'KJOEPE_FINBEVIS',
    OEKE_LAAN = 'OEKE_LAAN',
    FLYTTE_LAAN = 'FLYTTE_LAAN',
    BYGGE = 'BYGGE',
}

enum FormaalDelType {
    KJOEPE = 'KJOEPE',
    FIN_BEVIS = 'FIN_BEVIS',
}

type LaaneformaalFormData = {
    formaalType: FormaalType;
    formaalDelType: FormaalDelType;
};

const Laaneformaal: FC = () => {
    const { laaneformaal, button, validationMessage } = useTranslation();
    const { links, featureToggles } = useConfig();
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LaaneformaalFormData>();
    const visDelFormaal = useWatch({ name: 'formaalType', control });

    const laaneformaalOptions = () => {
        const options = [];
        if (!featureToggles.disableKjoepeBolig) {
            options.push({
                label: laaneformaal.selectValueKjoepBolig,
                value: FormaalType.KJOEPE_FINBEVIS,
                description: laaneformaal.infoKjoepBolig,
            });
        }
        options.push(
            {
                label: laaneformaal.selectValueOekelaan,
                value: FormaalType.OEKE_LAAN,
                description: laaneformaal.infoOekelaan,
            },
            {
                label: laaneformaal.selectValueFlytteLaan,
                value: FormaalType.FLYTTE_LAAN,
                description: laaneformaal.infoFlytteLaan,
            },
        );
        if (!featureToggles.disableByggelaan) {
            options.push({
                label: laaneformaal.selectValueByggeRenovereBolig,
                value: FormaalType.BYGGE,
                description: laaneformaal.infoByggeRenovereBolig,
            });
        }
        return options;
    };

    const onSubmitLaaneFormal = (data: LaaneformaalFormData) => {
        const urlMap = {
            [FormaalType.KJOEPE_FINBEVIS]:
                data.formaalDelType === FormaalDelType.KJOEPE ? links.kjoepeBolig.url : links.finansieringsbevis.url,
            [FormaalType.FLYTTE_LAAN]: links.flytteBoliglaan.url,
            [FormaalType.OEKE_LAAN]: links.okeBoliglaan.url,
            [FormaalType.BYGGE]: links.byggelaan.url,
        };
        window.location.replace(`${urlMap[data.formaalType]}`);
    };

    return (
        <div>
            <div className="sgw-layout margin-top-extra-large">
                <section className="sgw-layout-item sgw-layout-item__full-width">
                    <InfoCard asHeader img={<img src={husMSkyer} alt="Hus med skyer" />}>
                        <InfoCard.TitleH1>{laaneformaal.title}</InfoCard.TitleH1>
                        <p className="typography-ingress__light">{laaneformaal.subTitle}</p>
                    </InfoCard>
                </section>
            </div>
            <div className="sgw-layout medium-centered-container">
                <section className="sgw-layout-item margin-desktop-top-larger">
                    <form className="row row-gap-large" onSubmit={handleSubmit(onSubmitLaaneFormal)}>
                        <Controller
                            name="formaalType"
                            control={control}
                            rules={{
                                validate: { ikkeValgt: (value: FormaalType) => value !== undefined },
                            }}
                            render={({ field: { onChange, value } }) => (
                                <RadioButtonCards
                                    label={laaneformaal.labelLaaneformaal}
                                    selectedOption={value}
                                    onChange={(value) => onChange(value as FormaalType)}
                                    options={laaneformaalOptions()}
                                    errorMessage={
                                        errors.formaalType?.type === 'ikkeValgt'
                                            ? `${validationMessage.requiredChoose}`
                                            : ''
                                    }
                                />
                            )}
                        />
                        {visDelFormaal === FormaalType.KJOEPE_FINBEVIS && (
                            <Controller
                                name="formaalDelType"
                                control={control}
                                rules={{
                                    validate: { ikkeValgt: (value: FormaalDelType) => value !== undefined },
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <RadioButtonPanels
                                        label={laaneformaal.labelKjoepFinansieringsbevis}
                                        selectedOption={value}
                                        onChange={(value) => onChange(value as FormaalDelType)}
                                        options={[
                                            {
                                                label: laaneformaal.selectValueKonkretBolig,
                                                value: FormaalDelType.KJOEPE,
                                            },
                                            {
                                                label: laaneformaal.selectValueFinansieringsbevis,
                                                value: FormaalDelType.FIN_BEVIS,
                                            },
                                        ]}
                                        errorMessage={
                                            errors.formaalDelType?.type === 'ikkeValgt'
                                                ? `${validationMessage.requiredChoose}`
                                                : ''
                                        }
                                    />
                                )}
                            />
                        )}
                        <div className="row align-items margin-top-extra-large">
                            <div className="column column-gap">
                                <StandardButton
                                    buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                    onClick={() => window.history.back()}
                                >
                                    {button.forrige}
                                </StandardButton>
                                <StandardButton type="submit" buttonType={ButtonTypes.STANDARD_POSITIVE}>
                                    {button.neste}
                                </StandardButton>
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default Laaneformaal;
